import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, Pencil, Download } from "lucide-react";
import { toast } from "sonner";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { StatusPill, InServerBadge } from "@/components/network/StatusPill";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { KodeHistory } from "@/components/network/KodeHistory";
import { canEditInfra, CABLE_JENIS_LABEL, fmtLen, downloadKml } from "@/lib/network";

function Row({ k, children }) {
  return <div className="grid grid-cols-[110px_1fr] gap-2 border-b border-[#F3F4F6] py-1.5 text-xs"><span className="text-[#6B7280]">{k}</span><span>{children}</span></div>;
}

function EndLink({ e, kind, onSelect, testid }) {
  if (!e) return <span className="text-[#B45309]">Tidak ditemukan</span>;
  return <span className="flex flex-wrap items-center gap-1"><button className="font-semibold text-[#0055FF] hover:underline" onClick={() => onSelect({ kind, id: e.id })} data-testid={testid}>
    {kind.toUpperCase()} {e.kode} ({e.nama})</button>{e.in_server && <InServerBadge s={e.in_server} testid={`${testid}-inserver`} />}</span>;
}

export function CableDetailPanel({ sel, me, wilayahName, onClose, onSelect, onEdit, reloadKey, onDeleted }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/network/cables/${sel.id}`).then((r) => setD(r.data)).catch(() => toast.error("Gagal memuat detail kabel"));
  }, [sel, reloadKey]);
  const canEdit = d && canEditInfra(me) && d.from && d.to;
  return (
    <aside className="flex h-full flex-col bg-white" data-testid="cable-detail-panel">
      <div className="flex items-center justify-between border-b p-3">
        <b>Detail Jalur Kabel</b>
        <Button size="icon" variant="ghost" onClick={onClose} data-testid="cable-detail-close"><X className="h-4 w-4" /></Button>
      </div>
      {!d ? <p className="p-3 text-xs">Memuat…</p> : (
        <div className="flex-1 space-y-3 overflow-y-auto p-3">
          <div>
            <div className="font-display text-xl font-bold" data-testid="cable-detail-kode">{d.kode}</div>
            <div className="text-xs text-[#6B7280]">{d.nama || "-"} · {wilayahName(d.to?.wilayah_id)}</div>
          </div>
          <div>
            <Row k="Dari"><EndLink e={d.from} kind={d.from_kind} onSelect={onSelect} testid="cable-detail-from" /></Row>
            <Row k="Ke"><EndLink e={d.to} kind={d.to_kind || "odp"} onSelect={onSelect} testid="cable-detail-to" /></Row>
            <Row k="Jenis">{CABLE_JENIS_LABEL[d.jenis] || d.jenis}</Row>
            <Row k="Jumlah Core">{d.jumlah_core ? `${d.jumlah_core} core` : "-"}</Row>
            <Row k="Panjang"><span data-testid="cable-detail-length">{d.same_container ? "-" : fmtLen(d.panjang_m)}</span> <span className="text-[#6B7280]" data-testid="cable-detail-length-note">
              {d.same_container ? "(tidak dapat dihitung dari peta)" : !d.path?.length ? "(tidak dapat dihitung — koordinat ujung belum lengkap)" : d.panjang_perkiraan ? "(perkiraan — ujung di dalam SERVER/FDT)" : "(dari rute peta)"}</span></Row>
            <Row k="Belokan">{(d.route || []).length} titik</Row>
            <Row k="Status"><StatusPill status={d.status} /></Row>
            <Row k="Catatan">{d.catatan || "-"}</Row>
            <Row k="Diubah">{d.updated_by} · {(d.updated_at || "").slice(0, 10)}</Row>
          </div>
          <KodeHistory kind="cable" id={d.id} kode={d.kode} />
          {d.same_container && <p className="rounded border border-[#CBD5E1] bg-[#F1F5F9] p-2 text-xs text-[#1E293B]" data-testid="cable-detail-same-container">
            Kedua ujung berada di {d.same_container.label} <b className="font-mono-tab">{d.same_container.kode}</b> yang sama — jalur tidak digambar di peta. Data kabel tetap tersimpan.</p>}
          {!d.path?.length && !d.same_container && <p className="rounded bg-[#FFFBEB] p-2 text-xs text-[#B45309]" data-testid="cable-detail-nopath">
            Koordinat titik awal/tujuan tidak lengkap, jalur tidak dapat digambar.</p>}
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <ArahkanButton lat={d.from?.latitude} lng={d.from?.longitude} label="Arahkan ke Awal" testid="cable-nav-from" />
            <ArahkanButton lat={d.to?.latitude} lng={d.to?.longitude} label="Arahkan ke Tujuan" testid="cable-nav-to" />
            {d.path?.length > 0 && <Button size="sm" variant="outline" onClick={() => downloadKml([d.id], `jalur-kabel-${d.kode}.kml`)} data-testid="cable-kml-btn">
              <Download className="mr-1 h-3.5 w-3.5" />Unduh KML</Button>}
            {canEdit ? <Button size="sm" variant="outline" onClick={() => onEdit(d)} data-testid="cable-edit-btn">
              <Pencil className="mr-1 h-3.5 w-3.5" />Edit Rute & Data</Button>
              : <p className="text-center text-[10px] text-[#6B7280]">Edit hanya untuk Admin dan Koordinator</p>}
            <DeleteInfraButton kind="cable" item={d} me={me} onDeleted={onDeleted} />
          </div>
        </div>
      )}
    </aside>
  );
}
